// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
/** Pure analysis functions. No DOM, no fetch — all unit tested. */
import type { Postcode, National, Meta } from './types';
import { evShare, fuelShare } from './fuels';

/** Minimum passenger vehicles for a postcode to be ranked (drop noise). */
export const MIN_VEHICLES = 500;

export function median(values: number[]): number {
  const v = values.filter(Number.isFinite).slice().sort((a, b) => a - b);
  if (v.length === 0) return 0;
  const mid = Math.floor(v.length / 2);
  return v.length % 2 ? v[mid] : (v[mid - 1] + v[mid]) / 2;
}

export function quantile(values: number[], q: number): number {
  const v = values.filter(Number.isFinite).slice().sort((a, b) => a - b);
  if (v.length === 0) return 0;
  const pos = (v.length - 1) * Math.min(Math.max(q, 0), 1);
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  if (lo === hi) return v[lo];
  return v[lo] + (v[hi] - v[lo]) * (pos - lo);
}

/** A postcode's share of a fuel (0–100), null when it has too few vehicles. */
export function pcShare(p: Postcode, fuel: 'petrol' | 'diesel' | 'hybrid' | 'bev' | 'other'): number | null {
  return fuelShare(p, fuel);
}
export function pcEvShare(p: Postcode): number | null {
  return evShare(p);
}

/** Rankable postcodes (real denominator), unordered. */
export function rankable(all: Postcode[]): Postcode[] {
  return all.filter((p) => p.total >= MIN_VEHICLES);
}

export type Metric = 'bev' | 'ev' | 'diesel' | 'petrol' | 'total';

export function metricValue(p: Postcode, metric: Metric): number | null {
  switch (metric) {
    case 'bev':
      return pcShare(p, 'bev');
    case 'ev':
      return pcEvShare(p);
    case 'diesel':
      return pcShare(p, 'diesel');
    case 'petrol':
      return pcShare(p, 'petrol');
    case 'total':
      return p.total;
  }
}

/** Rankable postcodes sorted best-to-worst on a metric. */
export function rankedBy(all: Postcode[], metric: Metric): Postcode[] {
  return rankable(all)
    .filter((p) => metricValue(p, metric) != null)
    .sort((a, b) => (metricValue(b, metric) as number) - (metricValue(a, metric) as number));
}

export interface Bin {
  from: number;
  to: number;
  count: number;
  items: Postcode[];
}

/** Equal-width histogram binning on a metric. Values at/above max land in the last bin. */
export function histogram(items: Postcode[], value: (p: Postcode) => number | null, binCount: number, max: number): Bin[] {
  const width = max / binCount;
  const bins: Bin[] = Array.from({ length: binCount }, (_, i) => ({ from: i * width, to: (i + 1) * width, count: 0, items: [] }));
  for (const p of items) {
    const v = value(p);
    if (v == null || !Number.isFinite(v)) continue;
    let idx = Math.floor(v / width);
    if (idx >= binCount) idx = binCount - 1;
    if (idx < 0) idx = 0;
    bins[idx].count++;
    bins[idx].items.push(p);
  }
  return bins;
}

export type Severity = 'info' | 'warn' | 'alert' | 'good';
export interface Insight {
  severity: Severity;
  title: string;
  body: string;
  postcodes: string[];
}

/** Auto-detected findings, ordered most striking first. */
export function buildInsights(all: Postcode[], national: National, meta: Meta): Insight[] {
  const out: Insight[] = [];
  const n = meta.national;

  // 1. Diesel dwarfs electric.
  if (n.dieselToBevRatio != null) {
    out.push({
      severity: 'alert',
      title: `There is ${n.dieselToBevRatio.toFixed(1)}× more diesel than electric on the road`,
      body:
        `Battery-electric cars are just ${n.bevShare.toFixed(2)}% of the passenger fleet, while diesel is ` +
        `${n.dieselShare.toFixed(1)}%. Every EV headline is about a slice that is still a rounding error next to ` +
        `the quiet, decade-long rise of diesel — driven by the shift to utes and large SUVs.`,
      postcodes: [],
    });
  }

  // 2. Stock vs flow.
  if (n.newEvShare != null && n.newBevShare != null) {
    out.push({
      severity: 'info',
      title: `New cars are ${(n.newEvShare / Math.max(n.evShare, 0.1)).toFixed(1)}× more electrified than the fleet`,
      body:
        `Across the whole fleet, ${n.evShare.toFixed(1)}% of cars are electrified (hybrid or electric). Among cars ` +
        `built in ${meta.newestCompleteYom}, that jumps to ${n.newEvShare.toFixed(1)}% — and ${n.newBevShare.toFixed(1)}% ` +
        `are fully electric. The fleet is a slow-moving average of every year of car-buying still on the road.`,
      postcodes: [],
    });
  }

  // 3. The EV postcode divide.
  const evRanked = rankedBy(all, 'bev');
  if (evRanked.length >= 10) {
    const top = evRanked[0];
    const bottom = evRanked[evRanked.length - 1];
    const topShare = pcShare(top, 'bev') as number;
    const botShare = pcShare(bottom, 'bev') as number;
    out.push({
      severity: 'info',
      title: `The EV map is a wealth map: ${topShare.toFixed(1)}% vs ${botShare.toFixed(1)}%`,
      body:
        `${top.pc}${top.loc ? ` (${top.loc})` : ''} leads the country with ${topShare.toFixed(1)}% of its cars fully ` +
        `electric, against near-zero in much of regional Australia. Battery-electric uptake is concentrated in a ` +
        `handful of affluent inner-city postcodes and the ACT.`,
      postcodes: evRanked.slice(0, 4).map((p) => p.pc),
    });
  }

  // 4. Ageing fleet.
  if (n.medianYear != null && n.avgAgeYears != null) {
    const oldest = national.ageByState.filter((a) => a.medianYear != null).sort((a, b) => (a.medianYear as number) - (b.medianYear as number))[0];
    out.push({
      severity: 'warn',
      title: `The typical car on the road was built in ${n.medianYear}`,
      body:
        `Half of all passenger vehicles are older than model year ${n.medianYear} — about ${n.avgAgeYears} years old. ` +
        (oldest ? `${oldest.st} runs the oldest fleet (median ${oldest.medianYear}). ` : '') +
        `An older fleet burns more fuel and emits more, and it is the main reason the electric transition on the road ` +
        `lags the transition in the showroom.`,
      postcodes: [],
    });
  }

  // 5. Utes are diesel country.
  const lcv = national.byType.find((t) => t.type === 'Light commercial vehicles');
  if (lcv && lcv.total > 0) {
    out.push({
      severity: 'info',
      title: `Utes and vans are ${((lcv.diesel / lcv.total) * 100).toFixed(0)}% diesel`,
      body:
        `Australia's ${(lcv.total / 1e6).toFixed(1)} million light commercial vehicles are overwhelmingly diesel, and ` +
        `barely ${((lcv.bev / lcv.total) * 100).toFixed(2)}% are electric. Passenger cars are electrifying; the working ` +
        `fleet has hardly started.`,
      postcodes: [],
    });
  }

  // 6. Tesla dominance among EVs.
  const evMakes = national.makes.filter((m) => m.bev > 0).sort((a, b) => b.bev - a.bev);
  if (evMakes.length >= 3) {
    const tesla = evMakes[0];
    const totalBev = evMakes.reduce((s, m) => s + m.bev, 0);
    out.push({
      severity: 'good',
      title: `One brand is ${((tesla.bev / totalBev) * 100).toFixed(0)}% of every electric car`,
      body:
        `${tesla.make.charAt(0) + tesla.make.slice(1).toLowerCase()} alone accounts for ${tesla.bev.toLocaleString()} of the ` +
        `country's battery-electric cars — more than the next several brands combined. Chinese makers BYD and MG are the ` +
        `fastest-rising challengers.`,
      postcodes: [],
    });
  }

  return out;
}

/** National passenger fuel mix as an object for convenience. */
export function nationalShares(meta: Meta) {
  return meta.national;
}
